"use client"

import * as React from "react"
import { cn } from "@workspace/ui/lib/utils"
import { Input } from "@workspace/ui/components/input"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// Input Group Components (Compound Component Pattern)
// ============================================================================

/**
 * UIInputGroup
 * Container for input with addons (icons, buttons, text)
 * Based on Preline UI and Material UI InputGroup patterns
 */
export interface UIInputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export const UIInputGroup = React.forwardRef<HTMLDivElement, UIInputGroupProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <UIBox
        ref={ref}
        className={cn("relative", className)}
        {...props}
      >
        {children}
      </UIBox>
    )
  }
)
UIInputGroup.displayName = "UIInputGroup"

/**
 * UIInputGroupInput
 * Input element with proper padding for addons
 */
export interface UIInputGroupInputProps extends React.ComponentProps<typeof Input> {
  hasLeftAddon?: boolean
  hasRightAddon?: boolean
}

export const UIInputGroupInput = React.forwardRef<
  HTMLInputElement,
  UIInputGroupInputProps
>(({ className, hasLeftAddon, hasRightAddon, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      className={cn(
        hasLeftAddon && "pl-10",
        hasRightAddon && "pr-10",
        className
      )}
      {...props}
    />
  )
})
UIInputGroupInput.displayName = "UIInputGroupInput"

/**
 * UIInputGroupAddon
 * Addon container for icons, buttons, or text
 * Can be positioned on left or right side
 */
export interface UIInputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: "left" | "right"
  children: React.ReactNode
}

export const UIInputGroupAddon = React.forwardRef<
  HTMLDivElement,
  UIInputGroupAddonProps
>(({ className, position = "right", children, ...props }, ref) => {
  return (
    <UIBox
      ref={ref}
      className={cn(
        "absolute inset-y-0 flex items-center z-10",
        position === "left" ? "left-0 pl-3" : "right-0 pr-1",
        className
      )}
      {...props}
    >
      {children}
    </UIBox>
  )
})
UIInputGroupAddon.displayName = "UIInputGroupAddon"

// ============================================================================
// Type Exports
// ============================================================================

export type {
  UIInputGroupProps as InputGroupProps,
  UIInputGroupInputProps as InputGroupInputProps,
  UIInputGroupAddonProps as InputGroupAddonProps,
}